import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react";
import { css } from "@repo/styled-system/css";
import { ROOM_HEIGHT, ROOM_WIDTH, defaultSize } from "../editor/geometry";
import type { DraftTable } from "../editor/types";
import type { TableDragHandlers } from "./useTableDrag";

/**
 * One table rendered as a real, native HTML `<button>` absolutely positioned over the canvas
 * (Tech-decision "Canvas & drag tech"): a genuinely focusable/labelable/activatable DOM node — the
 * accessibility a bitmap-canvas library destroys. Mouse/touch drag via [dragHandlers]
 * (`setPointerCapture`), keyboard via [onKeyCommand] (arrow nudge, [/] rotate, Delete retire); Enter/
 * Space activate natively. Position/size are percentages of the room so the plan is device-independent.
 */

export type KeyCommand =
  | { type: "nudge"; dx: number; dy: number }
  | { type: "rotate"; delta: number }
  | { type: "retire" };

export interface TableShapeProps {
  table: DraftTable;
  accessibleName: string;
  selected: boolean;
  linkMode: boolean;
  isAnchor: boolean;
  paired: boolean;
  disabled: boolean;
  /** Live position while dragging (grid units); falls back to the table's persisted position. */
  ghostX?: number;
  ghostY?: number;
  dragHandlers?: TableDragHandlers;
  onActivate: () => void;
  onKeyCommand: (cmd: KeyCommand) => void;
}

export function TableShape(props: TableShapeProps) {
  const { table, accessibleName, selected, linkMode, isAnchor, paired, disabled } = props;
  const x = props.ghostX ?? table.x;
  const y = props.ghostY ?? table.y;
  const { w, h } = defaultSize(table.shape);

  function onKeyDown(e: ReactKeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        props.onKeyCommand({ type: "nudge", dx: 0, dy: e.shiftKey ? -50 : -10 });
        return;
      case "ArrowDown":
        e.preventDefault();
        props.onKeyCommand({ type: "nudge", dx: 0, dy: e.shiftKey ? 50 : 10 });
        return;
      case "ArrowLeft":
        e.preventDefault();
        props.onKeyCommand({ type: "nudge", dx: e.shiftKey ? -50 : -10, dy: 0 });
        return;
      case "ArrowRight":
        e.preventDefault();
        props.onKeyCommand({ type: "nudge", dx: e.shiftKey ? 50 : 10, dy: 0 });
        return;
      case "[":
        e.preventDefault();
        props.onKeyCommand({ type: "rotate", delta: -15 });
        return;
      case "]":
        e.preventDefault();
        props.onKeyCommand({ type: "rotate", delta: 15 });
        return;
      case "Delete":
      case "Backspace":
        e.preventDefault();
        props.onKeyCommand({ type: "retire" });
        return;
      default:
    }
  }

  const round = table.shape === "ROUND" || table.shape === "OVAL";
  const style: CSSProperties = {
    position: "absolute",
    left: `${(x / ROOM_WIDTH) * 100}%`,
    top: `${(y / ROOM_HEIGHT) * 100}%`,
    width: `${(w / ROOM_WIDTH) * 100}%`,
    height: `${(h / ROOM_HEIGHT) * 100}%`,
    transform: `translate(-50%, -50%) rotate(${table.rotationDeg}deg)`,
    borderRadius: round ? "50%" : table.shape === "CUSTOM" ? "22%" : "6px",
    opacity: table.retired ? 0.5 : 1,
  };

  return (
    <button
      type="button"
      aria-label={accessibleName}
      aria-pressed={selected}
      disabled={disabled}
      data-testid={`table-${table.id}`}
      data-selected={selected ? "true" : undefined}
      data-retired={table.retired ? "true" : undefined}
      style={style}
      onKeyDown={onKeyDown}
      onClick={() => props.onActivate()}
      {...(props.dragHandlers ?? {})}
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "sm",
        fontWeight: "medium",
        borderWidth: "2px",
        touchAction: "none",
        cursor: disabled ? "default" : linkMode ? "crosshair" : "grab",
        borderColor: selected || isAnchor ? "colorPalette.default" : "border.default",
        bg: table.retired ? "bg.muted" : paired ? "colorPalette.subtle" : "bg.subtle",
        color: "fg.default",
        _focusVisible: {
          outlineWidth: "2px",
          outlineStyle: "solid",
          outlineColor: "colorPalette.default",
        },
      })}
    >
      <span style={{ transform: `rotate(${-table.rotationDeg}deg)` }}>{table.label}</span>
    </button>
  );
}
